// Terms of Use copy shared by the upload form, sign-up, the sign-in terms check and the
// public /terms page. Whenever the terms text changes, bump TERMS_UPDATED (shown to people)
// and TERMS_VERSION, together with CURRENT_TERMS_VERSION in backend/app/terms.py (a backend
// test keeps the two equal). Signed-in users are then asked to accept the new version, and
// uploads record it.
export const TERMS_PATH = '/terms'
export const TERMS_UPDATED = '14 October 2026'
export const TERMS_VERSION = '2026-10-14'

// The public contact form: the site has no email address. Paragraphs in TERMS_SECTIONS can
// link to it (or any other page of the site) with [text](/path).
export const CONTACT_PATH = '/contact'
export const CONTACT_REASONS = [
  { value: 'copyright', label: 'Report content or copyright' },
  { value: 'remove_video', label: 'Remove a video of me or my child' },
  { value: 'delete_my_content', label: 'Delete my videos or account' },
  { value: 'account', label: 'Account help' },
  { value: 'other', label: 'Other' }
]

export const UPLOAD_DISCLAIMER = 'Upload only video you recorded yourself or have permission to upload for your personal use. You are responsible for what you upload, including the terms of any service or venue the video came from and the privacy of the other people in it. Highlights is a free personal project provided as is, and its AI analysis is experimental.'
export const UPLOAD_ATTESTATION = 'I own this video or have permission to upload it for my personal use and to have it analyzed, and I accept the Terms of Use.'
export const UPLOAD_ATTESTATION_NEEDED = 'Confirm you have the right to upload to continue'
export const SIGN_UP_TERMS_NEEDED = 'Agree to the Terms of Use to create an account.'

export const TERMS_SECTIONS = [
  {
    title: 'Who can use Highlights',
    body: [
      'You must be 18 or older, or the age of majority where you live, to create an account. Highlights is for parents and other adults. It is not directed to children, and children do not have accounts. If you believe a child has created an account, tell the site owner through the [contact form](/contact) and it will be closed.'
    ]
  },
  {
    title: 'About Highlights',
    body: [
      'Highlights is a free personal project run by an individual. It is not currently a business and charges no fees; if that changes, these terms will be updated first. It may change, pause or stop at any time, and stored videos may not be kept indefinitely, so keep your own copies.'
    ]
  },
  {
    title: 'Your uploads',
    body: [
      'Upload only video you recorded yourself or have permission to upload for your personal use and to have analyzed. By uploading, you confirm that you have that permission.',
      'You are solely responsible for the content you upload and for following the terms of any service, league, venue or person the video came from. Highlights is not affiliated with any camera, streaming, recording or league provider.',
      'Do not upload anything unlawful or anything that infringes someone else\'s rights, and never upload video recorded in locker rooms, changing rooms or bathrooms, or anything sexual, abusive or harassing. Such content will be removed and the account closed.'
    ]
  },
  {
    title: 'Other people in your videos',
    body: [
      'Game video usually shows other players, many of them minors, plus officials and spectators. Upload and share such video only where you are entitled to, and respect the privacy of the people in it.'
    ]
  },
  {
    title: 'How your videos are used',
    body: [
      'You keep ownership of your videos. You allow Highlights to store, copy, convert, process and display them to you and to the people you share them with, and to use them to run, fix and secure Highlights. Highlights will not use your videos to train its own AI models, or as test material, unless you agree to that separately. Highlights does not sell your videos.',
      'Videos are stored and processed on third-party cloud services. When you ask for analysis, frames from the video are sent to AI models hosted by those services.'
    ]
  },
  {
    title: 'Sharing',
    body: [
      'When you share a video or create a link, you are responsible for who receives it and for having the right to share everything it shows. Anyone who has a link can see its preview (title, description and a still image), and anyone who has a watch link can play it without signing in until you revoke it or it expires.'
    ]
  },
  {
    title: 'AI analysis is experimental',
    body: [
      'Player detection, AI highlights, coaching and any other automated analysis are experimental and can be wrong. Do not rely on them for recruiting, selection, medical, safety or other important decisions.'
    ]
  },
  {
    title: 'Removal of content',
    body: [
      'The site owner may remove any content or close any account at any time, for any reason, including in response to a complaint. To report content, to ask for video of you or your child to be removed, or to have your own videos or account deleted, use the [contact form](/contact) and describe the content and where it appears, with a link if you have one. You do not need an account to send a request. Highlights has no self-service delete yet, so the site owner deletes content on request.'
    ]
  },
  {
    title: 'Claims about your content',
    body: [
      'If anyone makes a claim against the site owner because of content you uploaded or shared, or because you broke these terms, you agree to cover the owner\'s resulting losses and reasonable costs, including legal fees, to the extent the law allows.'
    ]
  },
  {
    title: 'No warranty and limitation of liability',
    body: [
      'Highlights is provided "as is" and "as available", without warranties of any kind, express or implied, including fitness for a particular purpose, accuracy, availability and security. Highlights tries to keep videos private but cannot guarantee it.',
      'To the fullest extent the law allows, the site owner is not liable for any loss of data or content, or for any indirect, incidental, special or consequential damages, arising from your use of Highlights or from reliance on its analysis. In any case, the site owner\'s total liability to you for all claims is limited to US$50.'
    ]
  },
  {
    title: 'Governing law',
    body: [
      'These terms are governed by the laws of the Commonwealth of Virginia, USA, and any dispute will be heard in the state or federal courts located in Virginia, unless the law where you live requires otherwise.'
    ]
  },
  {
    title: 'Changes',
    body: [
      'These terms may change. The date at the top shows the latest version. After a change, you will be asked to accept the updated terms the next time you use Highlights, and uploading requires the current version.',
      'Highlights keeps a record of each acceptance, including at sign-up, at sign-in and with each upload: the terms version, the time, and the IP address and browser it came from.'
    ]
  },
  {
    title: 'Contact',
    body: [
      'To reach the site owner, including for removal and deletion requests, use the [contact form](/contact).'
    ]
  }
]
